import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://dear-bea-website.onrender.com';

const CATEGORIES = [
  'Ribbon',
  'Real',
  'Corsage',
  'BooBouquet',
  'Valentine',
];

const TAGS = [
  'Customizable',
  'Valentine',
  'Sold Out',
  'Ramo Style',
  'Custom Order',
  'Gift',
  'Seasonal',
];

const AdminAddProduct = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    compare_price: '',
    stock: '',
    category: '',
  });

  const [selectedTags, setSelectedTags] = useState([]);
  const [images, setImages] = useState([]); // Array of { file, preview }
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  // ── Helpers ──────────────────────────────────────────────

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleFiles = (files) => {
    const incoming = Array.from(files);
    const remaining = 6 - images.length;
    incoming.slice(0, remaining).forEach(file => {
      const preview = URL.createObjectURL(file);
      setImages(prev => [...prev, { file, preview }]);
    });
  };

  const removeImage = (index) => {
    setImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const clearForm = () => {
    setForm({ name: '', description: '', price: '', compare_price: '', stock: '', category: '' });
    setSelectedTags([]);
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
  };

  // ── Submit ────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.name.trim()) return showToast('error', 'Please enter a product name.');
    if (!form.price || isNaN(form.price) || parseFloat(form.price) <= 0)
      return showToast('error', 'Please enter a valid price.');
    if (!form.category) return showToast('error', 'Please select a category.');

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('description', form.description.trim());
      formData.append('price', parseFloat(form.price).toFixed(2));
      formData.append('compare_price', form.compare_price || '');
      formData.append('stock', form.stock || '');
      formData.append('category', form.category.toLowerCase());
      formData.append('tags', JSON.stringify(selectedTags.map(t => t.toLowerCase())));
      images.forEach(img => formData.append('images', img.file));

      const res = await fetch(`${API_URL}/api/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${window.adminToken}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);

      showToast('success', `"${form.name}" added successfully!`);
      clearForm();
    } catch (err) {
      showToast('error', err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-soft py-6 md:py-12">
      <div className="max-w-3xl mx-auto px-4">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-1">
              Add New Product
            </h1>
            <p className="text-gray-600 font-sans text-sm md:text-base">
              Fill in the details below to add a product to the store.
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="btn-primary self-start md:self-auto"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mb-6 p-4 rounded-xl font-sans text-sm font-semibold flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            <span>{toast.type === 'success' ? '✅' : '❌'}</span>
            {toast.message}
          </div>
        )}

        {/* Form Card */}
        <div className="card-romantic p-6 md:p-8 space-y-8">

          {/* Basic Info */}
          <section>
            <h2 className="text-lg font-display font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Basic Info
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                  Product name <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Seasonal Wildflower Bouquet"
                  maxLength={80}
                  className="input-romantic"
                />
                <p className="text-xs text-gray-400 font-sans mt-1 text-right">{form.name.length}/80</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Describe the arrangement, flowers included, customisation options..."
                  maxLength={600}
                  rows={4}
                  className="input-romantic resize-none"
                />
                <p className="text-xs text-gray-400 font-sans mt-1 text-right">{form.description.length}/600</p>
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section>
            <h2 className="text-lg font-display font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Pricing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                  Price (AUD) <span className="text-rose-600">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-sans text-sm">$</span>
                  <input
                    type="number"
                    name="price"
                    value={form.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="input-romantic pl-7"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                  Compare-at price
                  <span className="text-gray-400 font-normal ml-1">(optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-sans text-sm">$</span>
                  <input
                    type="number"
                    name="compare_price"
                    value={form.compare_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="input-romantic pl-7"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                  Stock quantity
                  <span className="text-gray-400 font-normal ml-1">(blank = unlimited)</span>
                </label>
                <input
                  type="number"
                  name="stock"
                  value={form.stock}
                  onChange={handleChange}
                  placeholder="Unlimited"
                  min="0"
                  step="1"
                  className="input-romantic"
                />
              </div>
            </div>
          </section>

          {/* Category & Tags */}
          <section>
            <h2 className="text-lg font-display font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Category & Tags
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 font-sans mb-1">
                Category <span className="text-rose-600">*</span>
              </label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="input-romantic"
              >
                <option value="">Select a category</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 font-sans mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold font-sans transition-all duration-200 border ${
                      selectedTags.includes(tag)
                        ? 'bg-rose-600 text-white border-rose-600 scale-105'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-rose-400 hover:text-rose-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {selectedTags.length > 0 && (
                <p className="text-xs text-gray-500 font-sans mt-2">
                  Selected: {selectedTags.join(', ')}
                </p>
              )}
            </div>
          </section>

          {/* Photos */}
          <section>
            <h2 className="text-lg font-display font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Photos
            </h2>

            {/* Drop zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
                images.length >= 6
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  : 'border-rose-300 hover:border-rose-500 hover:bg-rose-50 bg-white'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={images.length >= 6}
                onChange={e => handleFiles(e.target.files)}
              />
              <div className="text-4xl mb-3">📷</div>
              <p className="font-semibold text-gray-700 font-sans">
                {images.length >= 6 ? 'Maximum 6 photos reached' : 'Click or drag photos here'}
              </p>
              <p className="text-sm text-gray-500 font-sans mt-1">
                PNG, JPG up to 10MB each · up to 6 photos
              </p>
            </div>

            {/* Previews */}
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 group">
                    <img
                      src={img.preview}
                      alt={`Preview ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 bg-rose-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full font-sans">
                        Main
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full text-xs flex items-center justify-center transition"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={clearForm}
              disabled={loading}
              className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold font-sans hover:bg-gray-50 transition disabled:opacity-50"
            >
              Clear form
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold font-sans transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Adding product...
                </>
              ) : (
                '+ Add product'
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminAddProduct;
